/**
 * useViewPersistence Hook
 *
 * Provides consistent view state management with localStorage persistence
 * and optional window event listening for cross-component view synchronization.
 */
import { useState, useEffect, useRef } from 'react';
import { trackEvent } from '../utils/analytics';

/**
 * Manages view state with localStorage persistence and optional event listening.
 *
 * @param {string} storageKey - localStorage key for persisting the view
 * @param {string[]} validViews - Array of valid view values
 * @param {string} defaultView - Default view if none stored or invalid
 * @param {string} [eventName] - Optional window event name to listen for view changes
 * @returns {[string, Function]} Tuple of [currentView, setView]
 *
 * @example
 * // Basic usage
 * const [view, setView] = useViewPersistence('myPageView', ['table', 'chart'], 'table');
 *
 * @example
 * // With event listening for header menu integration
 * const [view, setView] = useViewPersistence(
 *   'industryMetricsView',
 *   ['table', 'chart', 'scatter'],
 *   'table',
 *   'metricsViewChange'
 * );
 */
export function useViewPersistence(storageKey, validViews, defaultView, eventName) {
  // Initialize state from localStorage, falling back to default if invalid
  const [view, setView] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored && validViews.includes(stored) ? stored : defaultView;
  });
  const previousView = useRef(view);

  // Persist view selection to localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, view);
    if (previousView.current !== view) {
      trackEvent('workspace_view_change', { view, cta_location: storageKey });
      previousView.current = view;
    }
  }, [view, storageKey]);

  // Listen for view changes from external events (e.g., header menu)
  useEffect(() => {
    if (!eventName) return;

    const handleViewChange = (event) => {
      const { view: newView } = event.detail;
      if (validViews.includes(newView)) {
        setView(newView);
      }
    };

    window.addEventListener(eventName, handleViewChange);
    return () => window.removeEventListener(eventName, handleViewChange);
  }, [eventName, validViews]);

  return [view, setView];
}

export default useViewPersistence;
